<script setup>
import { reactive, ref, onMounted, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import AiCloserAPI from 'dashboard/api/aiCloser';

const props = defineProps({
  product: {
    type: Object,
    default: () => ({}),
  },
  assistantId: {
    type: [Number, String],
    default: null,
  },
});
const store = useStore();
const { t } = useI18n();

const isSubmitting = ref(false);
const isLoading = ref(false);
const useDefaultData = ref(true);
const lastSavedSnapshot = ref('');

const snapshotFormState = () =>
  JSON.stringify({
    ...formData,
    use_default_data: useDefaultData.value,
  });

const syncFormState = productData => {
  Object.keys(formData).forEach(key => {
    formData[key] = productData?.[key] !== undefined ? productData[key] : '';
  });
  useDefaultData.value = productData?.use_default_data ?? true;
  lastSavedSnapshot.value = snapshotFormState();
};

const assistantRecord = computed(() =>
  store.getters?.['ailabAssistants/getRecord']
    ? store.getters['ailabAssistants/getRecord'](Number(props.assistantId))
    : null
);

const isAIGamer = computed(
  () => assistantRecord.value?.config?.wlab_sidebar_key === 'ai_gamer'
);

const formData = reactive({
  name: '',
  series: '',
  description: '',
  overview: '',
  key_ingredients: '',
  functions: '',
  suitable_for: '',
  not_suitable_for: '',
  suitable_for_vegetarians: '',
  suitable_for_vegans: '',
  suitable_for_halal_diet: '',
  side_effects: '',
  packaging: '',
  recommended_dosage: '',
  use_default_data: true,
});

// Fetch specific product by ID on mount
onMounted(async () => {
  if (!props.assistantId || !props.product?.id) return;
  isLoading.value = true;
  try {
    const response = await AiCloserAPI.getProductById(
      props.assistantId,
      props.product.id
    );
    const productData = response.data;
    if (productData) syncFormState(productData);
  } catch (error) {
    // Product may not exist yet, use props.product as fallback
    if (props.product) syncFormState(props.product);
  } finally {
    isLoading.value = false;
  }
});

// Watch for product changes (when user switches products)
watch(
  () => props.product?.id,
  async (newId, oldId) => {
    if (newId && newId !== oldId && props.assistantId) {
      isLoading.value = true;
      try {
        const response = await AiCloserAPI.getProductById(
          props.assistantId,
          newId
        );
        const productData = response.data;
        if (productData) syncFormState(productData);
      } catch (error) {
        // Reset form if product not found
        syncFormState({});
      } finally {
        isLoading.value = false;
      }
    }
  }
);

// Sync toggle with formData
watch(
  () => useDefaultData.value,
  newVal => {
    formData.use_default_data = newVal;
  }
);

const canSave = computed(
  () =>
    Boolean(
      props.assistantId &&
        props.product?.id &&
        !isLoading.value &&
        !isSubmitting.value &&
        snapshotFormState() !== lastSavedSnapshot.value
    )
);

const handleSave = async () => {
  if (!props.assistantId || !props.product?.id) return;
  isSubmitting.value = true;
  try {
    await store.dispatch('aiCloser/updateProductById', {
      assistantId: props.assistantId,
      productId: props.product.id,
      data: { ...formData },
    });
    lastSavedSnapshot.value = snapshotFormState();
    useAlert(t('AILAB.ASSISTANT_V2.PRODUCT_INFO.ALERTS.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.message ||
        t('AILAB.ASSISTANT_V2.PRODUCT_INFO.ALERTS.SAVE_FAILED')
    );
  } finally {
    isSubmitting.value = false;
  }
};

defineExpose({
  save: handleSave,
  canSave: () => canSave.value,
});
</script>

<template>
  <div class="info-tab">
    <!-- Toggle Switch -->
    <div class="toggle-row">
      <button
        class="crt-switch"
        :class="{ 'is-on': useDefaultData }"
        type="button"
        @click="useDefaultData = !useDefaultData"
      >
        <span />
      </button>
      <span class="toggle-label">
        {{
          isAIGamer
            ? t('AILAB.ASSISTANT_V2.PRODUCT_INFO.TOGGLE.LABEL_AIGAMER')
            : t('AILAB.ASSISTANT_V2.PRODUCT_INFO.TOGGLE.LABEL_DEFAULT')
        }}
      </span>
    </div>

    <!-- Form Container -->
    <div class="form-container" :class="{ 'is-default': useDefaultData }">
      <!-- Default Watermark -->
      <div v-if="useDefaultData" class="default-watermark">
        {{ t('AILAB.ASSISTANT_V2.PRODUCT_INFO.DEFAULT_WATERMARK') }}
      </div>

      <!-- Form Fields -->
      <div class="form-fields">
        <!-- Series -->
        <div class="form-group">
          <label class="form-label">
            {{
              isAIGamer
                ? t(
                    'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.SERIES.LABEL_AIGAMER'
                  )
                : t(
                    'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.SERIES.LABEL_DEFAULT'
                  )
            }}
          </label>
          <input
            v-model="formData.series"
            type="text"
            class="form-input"
            :disabled="useDefaultData"
            :placeholder="
              isAIGamer
                ? t(
                    'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.SERIES.PLACEHOLDER_AIGAMER'
                  )
                : undefined
            "
          />
          <p v-if="isAIGamer" class="field-help">
            {{
              t('AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.SERIES.HELP_AIGAMER')
            }}
          </p>
        </div>

        <!-- Description -->
        <div class="form-group">
          <label class="form-label">
            {{
              isAIGamer
                ? t(
                    'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.DESCRIPTION.LABEL_AIGAMER'
                  )
                : t(
                    'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.DESCRIPTION.LABEL_DEFAULT'
                  )
            }}
          </label>
          <div class="textarea-wrapper">
            <textarea
              v-model="formData.description"
              class="form-textarea"
              rows="5"
              :disabled="useDefaultData"
              :placeholder="
                isAIGamer
                  ? t(
                      'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.DESCRIPTION.PLACEHOLDER_AIGAMER'
                    )
                  : t(
                      'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.DESCRIPTION.PLACEHOLDER_DEFAULT'
                    )
              "
            />
            <button class="expand-btn">
              <span class="i-lucide-maximize-2 size-4" />
            </button>
          </div>
          <p v-if="isAIGamer" class="field-help">
            {{
              t(
                'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.DESCRIPTION.HELP_AIGAMER'
              )
            }}
          </p>
        </div>

        <!-- Overview -->
        <div class="form-group">
          <label class="form-label">
            {{
              isAIGamer
                ? t(
                    'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.OVERVIEW.LABEL_AIGAMER'
                  )
                : t(
                    'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.OVERVIEW.LABEL_DEFAULT'
                  )
            }}
          </label>
          <input
            v-model="formData.overview"
            type="text"
            class="form-input"
            :disabled="useDefaultData"
            :placeholder="
              isAIGamer
                ? t(
                    'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.OVERVIEW.PLACEHOLDER_AIGAMER'
                  )
                : t(
                    'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.OVERVIEW.PLACEHOLDER_DEFAULT'
                  )
            "
          />
          <p v-if="isAIGamer" class="field-help">
            {{
              t(
                'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.OVERVIEW.HELP_AIGAMER'
              )
            }}
          </p>
        </div>

        <!-- Key Ingredients -->
        <div class="form-group">
          <label class="form-label">
            {{
              isAIGamer
                ? t(
                    'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.KEY_INGREDIENTS.LABEL_AIGAMER'
                  )
                : t(
                    'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.KEY_INGREDIENTS.LABEL_DEFAULT'
                  )
            }}
          </label>
          <input
            v-model="formData.key_ingredients"
            type="text"
            class="form-input"
            :disabled="useDefaultData"
            :placeholder="
              isAIGamer
                ? t(
                    'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.KEY_INGREDIENTS.PLACEHOLDER_AIGAMER'
                  )
                : t(
                    'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.KEY_INGREDIENTS.PLACEHOLDER_DEFAULT'
                  )
            "
          />
          <p v-if="isAIGamer" class="field-help">
            {{
              t(
                'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.KEY_INGREDIENTS.HELP_AIGAMER'
              )
            }}
          </p>
        </div>

        <!-- Functions -->
        <div class="form-group">
          <label class="form-label">
            {{
              isAIGamer
                ? t(
                    'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.FUNCTIONS.LABEL_AIGAMER'
                  )
                : t(
                    'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.FUNCTIONS.LABEL_DEFAULT'
                  )
            }}
          </label>
          <input
            v-model="formData.functions"
            type="text"
            class="form-input"
            :disabled="useDefaultData"
            :placeholder="
              isAIGamer
                ? t(
                    'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.FUNCTIONS.PLACEHOLDER_AIGAMER'
                  )
                : t(
                    'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.FUNCTIONS.PLACEHOLDER_DEFAULT'
                  )
            "
          />
        </div>

        <!-- Suitable For -->
        <div class="form-group">
          <label class="form-label">
            {{
              isAIGamer
                ? t(
                    'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.SUITABLE_FOR.LABEL_AIGAMER'
                  )
                : t(
                    'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.SUITABLE_FOR.LABEL_DEFAULT'
                  )
            }}
          </label>
          <input
            v-model="formData.suitable_for"
            type="text"
            class="form-input"
            :disabled="useDefaultData"
            :placeholder="
              isAIGamer
                ? t(
                    'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.SUITABLE_FOR.PLACEHOLDER_AIGAMER'
                  )
                : t(
                    'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.SUITABLE_FOR.PLACEHOLDER_DEFAULT'
                  )
            "
          />
        </div>

        <!-- Not Suitable For -->
        <div class="form-group">
          <label class="form-label">
            {{
              isAIGamer
                ? t(
                    'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.NOT_SUITABLE_FOR.LABEL_AIGAMER'
                  )
                : t(
                    'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.NOT_SUITABLE_FOR.LABEL_DEFAULT'
                  )
            }}
          </label>
          <input
            v-model="formData.not_suitable_for"
            type="text"
            class="form-input"
            :disabled="useDefaultData"
            :placeholder="
              isAIGamer
                ? t(
                    'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.NOT_SUITABLE_FOR.PLACEHOLDER_AIGAMER'
                  )
                : t(
                    'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.NOT_SUITABLE_FOR.PLACEHOLDER_DEFAULT'
                  )
            "
          />
        </div>

        <!-- Suitable For Vegetarians -->
        <div v-if="!isAIGamer" class="form-group">
          <label class="form-label">{{
            t('AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.VEGETARIANS.LABEL')
          }}</label>
          <input
            v-model="formData.suitable_for_vegetarians"
            type="text"
            class="form-input"
            :disabled="useDefaultData"
          />
        </div>

        <!-- Suitable For Vegans -->
        <div v-if="!isAIGamer" class="form-group">
          <label class="form-label">{{
            t('AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.VEGANS.LABEL')
          }}</label>
          <input
            v-model="formData.suitable_for_vegans"
            type="text"
            class="form-input"
            :disabled="useDefaultData"
          />
        </div>

        <!-- Suitable For Halal Diet -->
        <div v-if="!isAIGamer" class="form-group">
          <label class="form-label">{{
            t('AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.HALAL.LABEL')
          }}</label>
          <input
            v-model="formData.suitable_for_halal_diet"
            type="text"
            class="form-input"
            :disabled="useDefaultData"
          />
        </div>

        <!-- Side Effects -->
        <div class="form-group">
          <label class="form-label">
            {{
              isAIGamer
                ? t(
                    'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.SIDE_EFFECTS.LABEL_AIGAMER'
                  )
                : t(
                    'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.SIDE_EFFECTS.LABEL_DEFAULT'
                  )
            }}
          </label>
          <input
            v-model="formData.side_effects"
            type="text"
            class="form-input"
            :disabled="useDefaultData"
            :placeholder="
              isAIGamer
                ? t(
                    'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.SIDE_EFFECTS.PLACEHOLDER_AIGAMER'
                  )
                : undefined
            "
          />
          <p v-if="isAIGamer" class="field-help">
            {{
              t(
                'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.SIDE_EFFECTS.HELP_AIGAMER'
              )
            }}
          </p>
        </div>

        <!-- Packaging -->
        <div class="form-group">
          <label class="form-label">
            {{
              isAIGamer
                ? t(
                    'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.PACKAGING.LABEL_AIGAMER'
                  )
                : t(
                    'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.PACKAGING.LABEL_DEFAULT'
                  )
            }}
          </label>
          <input
            v-model="formData.packaging"
            type="text"
            class="form-input"
            :disabled="useDefaultData"
            :placeholder="
              isAIGamer
                ? t(
                    'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.PACKAGING.PLACEHOLDER_AIGAMER'
                  )
                : t(
                    'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.PACKAGING.PLACEHOLDER_DEFAULT'
                  )
            "
          />
        </div>

        <!-- Recommended Dosage -->
        <div class="form-group">
          <label class="form-label">
            {{
              isAIGamer
                ? t(
                    'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.RECOMMENDED_DOSAGE.LABEL_AIGAMER'
                  )
                : t(
                    'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.RECOMMENDED_DOSAGE.LABEL_DEFAULT'
                  )
            }}
          </label>
          <input
            v-model="formData.recommended_dosage"
            type="text"
            class="form-input"
            :disabled="useDefaultData"
            :placeholder="
              isAIGamer
                ? t(
                    'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.RECOMMENDED_DOSAGE.PLACEHOLDER_AIGAMER'
                  )
                : t(
                    'AILAB.ASSISTANT_V2.PRODUCT_INFO.FIELDS.RECOMMENDED_DOSAGE.PLACEHOLDER_DEFAULT'
                  )
            "
          />
        </div>
      </div>

      <!-- Save Button -->
      <div v-if="!useDefaultData" class="form-actions">
        <button class="save-btn" :disabled="!canSave" @click="handleSave">
          <span
            v-if="isSubmitting"
            class="i-lucide-loader-2 size-4 animate-spin"
          />
          <span v-else class="i-lucide-save size-4" />
          {{ t('AILAB.ASSISTANT_V2.PRODUCT_INFO.ACTIONS.SAVE') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.info-tab {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-bottom: 80px;
}

.toggle-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: var(--wlab-bg-tertiary);
  border: 1px solid var(--wlab-border-light);
  border-radius: 10px;
}

.toggle-switch {
  position: relative;
  width: 44px;
  height: 24px;
  cursor: pointer;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  inset: 0;
  background: var(--wlab-text-muted);
  border-radius: 24px;
  transition: 0.3s;
}

.toggle-slider::before {
  content: '';
  position: absolute;
  width: 18px;
  height: 18px;
  left: 3px;
  bottom: 3px;
  background: white;
  border-radius: 50%;
  transition: 0.3s;
}

.toggle-switch input:checked + .toggle-slider {
  background: rgba(255, 255, 255, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.5);
}

:global(body:not(.dark) .toggle-switch input:checked + .toggle-slider) {
  background: rgba(0, 102, 255, 0.25);
  border-color: rgba(0, 102, 255, 0.6);
}

:global(body.dark .toggle-switch input:checked + .toggle-slider) {
  background: rgba(255, 255, 255, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.5);
}

.toggle-switch input:checked + .toggle-slider::before {
  transform: translateX(20px);
  background: #ffffff;
}

:global(body:not(.dark) .toggle-switch input:checked + .toggle-slider::before) {
  background: #0066ff;
}

:global(body.dark .toggle-switch input:checked + .toggle-slider::before) {
  background: #ffffff;
}

.toggle-label {
  font-size: 14px;
  color: var(--wlab-text-secondary);
}

.form-container {
  position: relative;
  padding: 20px;
  background: var(--wlab-bg-tertiary);
  border: 1px solid var(--wlab-border-light);
  border-radius: 12px;
}

.form-container.is-default {
  opacity: 0.7;
}

.default-watermark {
  position: absolute;
  right: 3%;
  bottom: 5%;
  font-size: 5rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.08);
  pointer-events: none;
  z-index: 1;
}

.form-fields {
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;
  z-index: 2;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--wlab-text-secondary);
  line-height: 1.4;
}

.field-help {
  margin-top: 2px;
  font-size: 12px;
  color: var(--wlab-text-muted);
  line-height: 1.4;
}

.form-input {
  width: 100%;
  padding: 12px 14px;
  font-size: 14px;
  color: var(--wlab-text-primary);
  background: var(--wlab-bg-tertiary);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  transition: all 0.2s;
}

.form-input:focus {
  outline: none;
  border-color: rgba(255, 255, 255, 0.2);
}

.form-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.form-input::placeholder {
  color: var(--wlab-text-muted);
}

.textarea-wrapper {
  position: relative;
}

.form-textarea {
  width: 100%;
  padding: 12px 14px;
  font-size: 14px;
  color: var(--wlab-text-primary);
  background: var(--wlab-bg-tertiary);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  resize: none;
}

.form-textarea:focus {
  outline: none;
  border-color: rgba(255, 255, 255, 0.2);
}

.form-textarea:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.expand-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 6px;
  color: var(--wlab-text-tertiary);
  cursor: pointer;
}

.expand-btn:hover {
  background: var(--wlab-border-default);
  color: var(--wlab-text-primary);
}

.form-actions {
  position: sticky;
  bottom: 0;
  z-index: 10;
  background: var(--wlab-bg-tertiary);
  border-top: 1px solid var(--wlab-border-light);
  padding: 16px 0;
  margin-top: 20px;
}

.save-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  background: #ffffff;
  color: #000000;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
}

:global(body:not(.dark) .save-btn) {
  background: #0066ff;
  color: #ffffff;
}

:global(body.dark .save-btn) {
  background: #ffffff;
  color: #000000;
  box-shadow: 0 2px 8px rgba(255, 255, 255, 0.1);
}

.save-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(0, 102, 255, 0.4);
}

.save-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
